import { useEffect, useMemo } from 'react';

import { useIsGuestFacingApp, useIsInAdminPreviewIframe } from '@shared/core/router.helper';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { BOOT_FLAG_HIDE_JOY_LOGO, useHideJoyLogoBootFlag } from '@shared/core/bootFlags';
import { addError } from '@shared/utils/logger';

/**
 * Whether the Joy logo may be shown on this surface at boot.
 *
 * The loader decision must be made synchronously from data the server already provided —
 * no async, no network — and must never change after first paint (no spinner swapping).
 *
 * Rules:
 * - Host-facing apps (admin, card flows, acquisition, account, ...): always Joy branding.
 * - Guest-facing apps: Joy branding unless the server-embedded boot flags say hidejoylogo is
 *   on. An absent value means the server failed to deliver boot flags — reported as an error
 *   and resolved to generic branding (fail safe toward white-label customers, who must never
 *   see the Joy logo).
 *
 * Do not use this hook for late-pipeline UI (footers, content). Those surfaces can track the
 * live Amplitude flag; use useJoyBranding instead.
 */
export const useBootBranding = (): Readonly<{ showJoyLogo: boolean }> => {
  const isGuestFacing = useIsGuestFacingApp();
  const hideJoyLogoBootFlag = useHideJoyLogoBootFlag();

  const isInAdminPreviewIframe = useIsInAdminPreviewIframe();

  // Derived state: both inputs are constant within a document — the route's declaration and the
  // page's boot flags — so the loader never swaps mid-boot, while a client-side navigation to a
  // different surface gets that surface's branding.
  const branding = useMemo(() => {
    if (!isGuestFacing) {
      return { showJoyLogo: true, bootFlagsMissing: false };
    }
    // Admin iframe previews load guest-facing routes (?preview=true&ctx=...) so the host can
    // see the guest site while editing. The viewer is the host, not a guest, so the loader
    // always shows Joy branding there; the page content inside still follows the flag.
    if (isInAdminPreviewIframe) {
      return { showJoyLogo: true, bootFlagsMissing: false };
    }
    if (hideJoyLogoBootFlag === undefined) {
      return { showJoyLogo: false, bootFlagsMissing: true };
    }
    return { showJoyLogo: !hideJoyLogoBootFlag, bootFlagsMissing: false };
  }, [isGuestFacing, isInAdminPreviewIframe, hideJoyLogoBootFlag]);

  const { showJoyLogo, bootFlagsMissing } = branding;

  useEffect(() => {
    if (bootFlagsMissing) {
      // The server embeds resolved boot flags on every render, so an absent value on a
      // guest-facing surface is a server failure, not a client state to tolerate.
      addError(new Error('Boot flags missing on guest-facing surface'), {
        context: 'useBootBranding',
        flag: BOOT_FLAG_HIDE_JOY_LOGO,
      });
    }
  }, [bootFlagsMissing]);

  return useMemo(() => ({ showJoyLogo }), [showJoyLogo]);
};

/**
 * Branding decision for surfaces that render later in the pipeline (footers, upload album).
 * Intersects the same two inputs as the boot decision — the app's isGuestFacing declaration and
 * the hideJoyLogo flag — but adopts the live Amplitude value once it resolves, since these
 * components mount well after flag resolution and should track its true value.
 *
 * While the live flag is loading it falls back to the server-embedded boot flag, so these
 * surfaces render their final state on first paint instead of appearing a tick late.
 */
export const useJoyBranding = (): Readonly<{ showJoyLogo: boolean; resolved: boolean }> => {
  const isGuestFacing = useIsGuestFacingApp();
  const hideJoyLogoBootFlag = useHideJoyLogoBootFlag();
  const [hideJoyLogo, hideJoyLogoLoading] = useIsFeatureEnabled('hideJoyLogo');

  return useMemo(() => {
    if (!isGuestFacing) {
      return { showJoyLogo: true, resolved: true };
    }
    if (hideJoyLogoLoading) {
      if (hideJoyLogoBootFlag === undefined) {
        return { showJoyLogo: false, resolved: false };
      }
      return { showJoyLogo: !hideJoyLogoBootFlag, resolved: true };
    }
    return { showJoyLogo: !hideJoyLogo, resolved: true };
  }, [isGuestFacing, hideJoyLogoBootFlag, hideJoyLogo, hideJoyLogoLoading]);
};
